import React, { useEffect, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCalendarDays } from '@fortawesome/free-regular-svg-icons'
import { faGlobe } from '@fortawesome/free-solid-svg-icons'
import { faDirections } from '@fortawesome/free-solid-svg-icons'
import LeaveSiteWarningModal from './LeaveSiteWarningModal'

class ClinicListModel {

    constructor(clinics) {                        
        this.ClosestIndex = clinics.ClosestIndex;        
        this.Clinics = clinics.FacilityList.map(f => new ClinicModel(f)).sort((a, b) => a.CompareByDistance(b));
    }
}

class ClinicModel {
    constructor(facility) {
        this.FacilityId = facility.FacilityID;
        this.VenueId = facility.VenueId;
        this.Name = facility.FacilityName;
        this.Website = facility.WebAddress;
        this.DistanceRounded = facility.DistanceToPostcode;
        this.Distance = facility.GoogleDistance;        
        this.Address = new ClinicAddressModel(facility);
        this.Latitude = facility.Latitude;
        this.Longitude = facility.Longitude;
        this.TelephoneNumber = facility.TelephoneNumber;
        this.FirstAppointment = facility.FirstAppointment;
        this.IsBookable = Array.isArray(facility.AvailableSlots);
        this.HasAppointments = Array.isArray(facility.AvailableSlots) && facility.AvailableSlots.length > 0;

        const wa = facility.WebAddress;

        if(typeof wa === 'string' && wa !== '') {

            try {
                this.WebAddress = new URL(wa).href; 
            }
            catch {
                this.WebAddress = "https://" + wa;
            }            
        }
        else {
            this.WebAddress = null;
        }
    }

    CompareByDistance(otherClinicModel) {
        if(this.Distance > otherClinicModel.Distance) {
            return 1;
        }
        else if(this.Distance < otherClinicModel.Distance) {
            return -1;
        }
        else {
            return 0;
        }
    }

    get NextAppointmentDisplayDateTime() {        
        const dt = new Date(this.FirstAppointment);

        if(dt instanceof Date && !isNaN(dt))
        {
            const formatoptions = 
            {
                hour12: false,
                month: "short", 
                day: "2-digit", 
                hour: "2-digit", 
                minute: "2-digit"
            };

            return dt.toLocaleString("en-GB", formatoptions); 
        }
        else
        {
            console.error("invalid next appointment datetime " + this.FirstAppointment + " for clinic " + this.FacilityId);
            return null;
        }
    }

    get DirectionsSelection() {
        return  {
            coordinate: { Latitude: this.Latitude, Longitude: this.Longitude }, 
            address: { FacilityName: this.Name, Address1: this.Address.Line1, Address2: this.Address.Line2, Address3: this.Address.Line3, Postcode: this.Postcode, FacilityID: this.FacilityId, Bookable: this.HasAppointments }
        }
    }
}

class ClinicAddressModel {

    constructor(facility) {
        this.Line1 = facility.Address1;
        this.Line2 = facility.Address2;
        this.Line3 = facility.Address3;
        this.Town = facility.Town;
        this.Postcode = facility.Postcode;
        this.County = facility.County;
        this.Country = facility.Country;
    }

    get Summary() {
        return this.Line1 + ", " + this.Town + ", " + this.Postcode;
    }
}

const listItemName = "referral-list-item-";

export default function ReferralList({clinics, mapViewMode, selectClinic, selectClinicSlots, activeMarker, selectDirections}) {

    let lst = new ClinicListModel(clinics);

    const [pendingWebsiteUrl, setPendingWebsiteUrl] = useState(null);

    const confirmLeaveSite = () => {
        window.open(pendingWebsiteUrl, '_blank', 'noopener,noreferrer');
        setPendingWebsiteUrl(null);
    };

    useEffect(() =>
    {   
        let listelement = document.getElementById("referral-list-items");
        let elementToHighlight = null; 

        if(activeMarker !== null) {
            elementToHighlight = listItemName + activeMarker.FacilityID;            
        }

        for (const itemElement of listelement.children) {
            
            if(itemElement.id === elementToHighlight) {
                
                itemElement.className = "referral-clinic-list-item referral-clinic-list-item-highlight";
                itemElement.scrollIntoView({behavior: "smooth", block: "nearest", inline: "nearest"});                            
            }
            else {
                itemElement.className = "referral-clinic-list-item";
            }
        }        
        
    }, [activeMarker]);
    
    return  (
        <>
        <div id="referral-list-items" className={mapViewMode.liststyle}>
            {
                lst.Clinics.map((clinic) =>                 
                    (                
                        <div id={listItemName + clinic.FacilityId} className="referral-clinic-list-item">
                            <h1>{clinic.Name}</h1>
                            <h2>{clinic.Address.Summary}</h2>
                            <h2 className='referral-clinic-list-item-detail-bold'>{clinic.DistanceRounded.toFixed(1)} mi</h2>                        
                            <br />

                            {
                                clinic.WebAddress &&
                                <span className='referral-clinic-list-item-info' onClick={() => setPendingWebsiteUrl(clinic.WebAddress)}>
                                    <FontAwesomeIcon icon={faGlobe} className='referral-clinic-list-item-info-icon' />
                                    <span className='link-button'>Visit Website</span>
                                </span>
                            }
                            {
                                <span className='referral-clinic-list-item-info' onClick={() => selectDirections(clinic.DirectionsSelection)}>
                                    <FontAwesomeIcon icon={faDirections} className='referral-clinic-list-item-info-icon' />
                                    <span className='link-button'>Directions</span>                                                                                            
                                </span>
                            }
                            <br />
                            {
                                clinic.HasAppointments && 
                                <span className='referral-clinic-list-item-info'>
                                    <FontAwesomeIcon icon={faCalendarDays} className='referral-clinic-list-item-info-icon' />
                                    <span>Next Available: {clinic.NextAppointmentDisplayDateTime}</span>                                                            
                                </span>
                            }                        
                            <br />
                            <div className='referral-clinic-list-item-buttons'>
                                <button type='button' className='green-button' onClick={(e)=>selectClinic(e, clinic.FacilityId, clinic.Name, clinic.Address.Summary)}><span>Select Clinic</span></button>
                                {
                                    clinic.IsBookable && <button type='button' className={clinic.HasAppointments ? 'green-button' : 'grey-button'} disabled={!clinic.HasAppointments} onClick={(e)=>selectClinicSlots(e, clinic.FacilityId, clinic.VenueId, clinic.Name,clinic.Address.Summary)}><FontAwesomeIcon icon={faCalendarDays} className='referral-clinic-list-item-info-icon' /><span>Book Online</span></button>
                                }
                            </div>
                            
                        </div>                
                    )
                )
            }

        </div>

        {
            pendingWebsiteUrl &&
            <LeaveSiteWarningModal
                onConfirm={confirmLeaveSite}
                onCancel={() => setPendingWebsiteUrl(null)}
            />
        }
        </>
    )
}